import {
  DOMConversionMap,
  EditorConfig,
  LexicalEditor,
  LexicalNode,
  SerializedElementNode,
} from 'lexical';

import { addClassNamesToElement } from '@lexical/utils';
import { LayoutItemNode } from './LayoutItemNode';
import { $isLocalizationNode } from './Localizable/LocalizationNode.tsx';
import { createActionButton } from '../utils/actionButton.ts';
import { $isTabContainerNode } from './TabContainerNode.ts';

export type SerializedTabItemNode = SerializedElementNode;

export class TabItemNode extends LayoutItemNode {
  static getType(): string {
    return 'tab-item';
  }

  static clone(node: TabItemNode): TabItemNode {
    return new TabItemNode(node.__key);
  }

  createDOM(config: EditorConfig, editor: LexicalEditor): HTMLDivElement {
    const dom = document.createElement('div') as HTMLDivElement;
    const removeNode = () => {
      editor.update(() => {
        const parentElement = this.getParent();

        this.remove();

        if ($isTabContainerNode(parentElement)) {
          const str = parentElement.getTemplateColumns();
          const shortenedStr = str.slice(0, str.lastIndexOf(' '));
          const writableNode = parentElement.getWritable();
          writableNode.setTemplateColumns(shortenedStr);
        }

        if (parentElement) {
          const children = parentElement.getChildren();

          if (children.length === 1 && $isLocalizationNode(children.at(0))) {
            parentElement.remove();
          }
        }
      });
    };

    const swapNode = () => {
      editor.update(() => {
        const nextSibling = this?.getNextSibling();

        if ($isTabItemNode(nextSibling) && $isTabItemNode(this)) {
          const currentChildren = this.getChildren();
          const nextChildren = nextSibling.getChildren();

          currentChildren.forEach((child) => child.remove());
          nextChildren.forEach((child) => child.remove());

          this.append(...nextChildren);
          nextSibling.append(...currentChildren);
        }
      });
    };

    dom.append(createActionButton(removeNode, 'tabItemRemoveButton'));
    dom.append(createActionButton(swapNode, 'tabItemSwapButton'));

    if (typeof config.theme.tabItem === 'string') {
      addClassNamesToElement(dom, config.theme.tabItem);
    }
    return dom;
  }

  static importDOM(): DOMConversionMap | null {
    return {
      div: (domNode: HTMLElement) => {
        if (domNode.classList.contains('PlaygroundEditorTheme__tabItem')) {
          return {
            conversion: () => ({ node: $createTabItemNode() }),
            priority: 3,
          };
        }
        return null;
      },
    };
  }

  static importJSON(): TabItemNode {
    return $createTabItemNode();
  }

  exportJSON(): SerializedTabItemNode {
    return {
      ...super.exportJSON(),
      type: 'tab-item',
      version: 1,
    };
  }
}

export function $createTabItemNode(): TabItemNode {
  return new TabItemNode();
}

export function $isTabItemNode(
  node: LexicalNode | null | undefined,
): node is TabItemNode {
  return node instanceof TabItemNode;
}
